import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
    Layers, Edit3, Wand2, ChevronDown, ChevronRight,
    Save, X, Loader2, RefreshCw, CheckCircle2, AlertCircle,
    Download, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import api, { BACKEND_URL } from '../utils/api';

/**
 * SectionEditor – Section-level edit & AI regeneration panel.
 *
 * Connects to the backend section APIs:
 *   GET  /api/projects/{id}/sections?path=<file>
 *   POST /api/projects/{id}/sections/edit
 *   POST /api/projects/{id}/sections/regenerate
 *
 * Props:
 *   projectId  – current project id
 *   files      – array of { path, content } for page selection
 *   onUpdate   – callback after a section is saved (receives updated HTML)
 */
const SectionEditor = ({ projectId, files, onUpdate }) => {
    const [currentFile, setCurrentFile] = useState('index.html');
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Per-section state
    const [expandedSection, setExpandedSection] = useState(null);
    const [editMode, setEditMode] = useState(null); // 'html' | 'regen' | null
    const [editHtml, setEditHtml] = useState('');
    const [regenInstruction, setRegenInstruction] = useState('');
    const [saving, setSaving] = useState(false);

    // List of HTML pages available
    const htmlPages = (files || [])
        .map((f) => f.path || '')
        .filter((p) => p.endsWith('.html'));

    // ── Fetch sections ───────────────────────────────────────────
    const fetchSections = useCallback(async () => {
        if (!projectId || !currentFile) return;
        setLoading(true);
        setError(null);
        try {
            const res = await api.get(`/projects/${projectId}/sections`, {
                params: { path: currentFile },
            });
            setSections(res.data.sections || []);
        } catch (err) {
            const msg = err.response?.data?.detail || err.message || 'Failed to load sections';
            setError(msg);
            setSections([]);
        } finally {
            setLoading(false);
        }
    }, [projectId, currentFile]);

    useEffect(() => {
        fetchSections();
    }, [fetchSections]);

    // ── Extract current inner HTML for a section (client-side) ──
    const extractSectionHtml = useCallback(
        (sectionName) => {
            const file = (files || []).find((f) => f.path === currentFile);
            if (!file) return '';
            const html = file.content || '';
            // Match the section marker and extract inner content
            const patterns = [
                new RegExp(
                    `(<section[^>]*(?:id|data-section)=["']${sectionName}["'][^>]*>)([\\s\\S]*?)(</section>)`,
                    'i'
                ),
                new RegExp(
                    `(<!--\\s*${sectionName}\\s*-->)([\\s\\S]*?)(<!--\\s*/${sectionName}\\s*-->)`,
                    'i'
                ),
            ];
            for (const pattern of patterns) {
                const m = html.match(pattern);
                if (m) return m[2];
            }
            return '<!-- Could not extract section -->';
        },
        [files, currentFile]
    );

    // ── Toggle expand + pre-fill edit HTML ──────────────────────
    const handleToggle = (name) => {
        if (expandedSection === name) {
            setExpandedSection(null);
            setEditMode(null);
            return;
        }
        setExpandedSection(name);
        setEditMode(null);
        setEditHtml(extractSectionHtml(name));
        setRegenInstruction('');
    };

    // ── Save direct HTML edit ──────────────────────────────────
    const handleSaveHtml = async () => {
        if (!projectId || !expandedSection) return;
        setSaving(true);
        try {
            const res = await api.post(`/projects/${projectId}/sections/edit`, {
                file_path: currentFile,
                section_name: expandedSection,
                new_inner_html: editHtml,
            });
            if (res.data.success) {
                toast.success(`Section "${expandedSection}" updated`);
                onUpdate && onUpdate(currentFile, expandedSection);
                setEditMode(null);
            }
        } catch (err) {
            const msg = err.response?.data?.detail || 'Save failed';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    // ── AI Regenerate section ──────────────────────────────────
    const handleRegenerate = async () => {
        if (!projectId || !expandedSection || !regenInstruction.trim()) return;
        setSaving(true);
        try {
            const res = await api.post(`/projects/${projectId}/sections/regenerate`, {
                file_path: currentFile,
                section_name: expandedSection,
                instruction: regenInstruction.trim(),
            });
            if (res.data.success) {
                toast.success(`Section "${expandedSection}" regenerated with AI`);
                onUpdate && onUpdate(currentFile, expandedSection);
                setEditMode(null);
                setRegenInstruction('');
                // Refresh section list (may have changed)
                fetchSections();
            }
        } catch (err) {
            const msg = err.response?.data?.detail || 'Regeneration failed';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    // ── Section display name ───────────────────────────────────
    const formatSectionName = (name) =>
        name
            .replace(/[-_]/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());

    // ── Export ZIP shortcut ────────────────────────────────────
    const [exporting, setExporting] = useState(false);
    const handleExportZip = async () => {
        if (!projectId) return;
        setExporting(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${BACKEND_URL}/api/projects/${projectId}/export/repo`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || `Export failed (${res.status})`);
            }
            const blob = await res.blob();
            const disposition = res.headers.get('content-disposition') || '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : `${projectId}.zip`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('Deploy-ready ZIP downloaded');
        } catch (err) {
            toast.error(err.message || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    // ── Render ─────────────────────────────────────────────────
    return (
        <div className="h-full flex flex-col bg-slate-950 text-white">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-4 py-2.5 border-b border-slate-700/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-purple-400" />
                    <span className="text-sm font-medium text-slate-300">Section Editor</span>
                </div>
                <div className="flex items-center gap-2">
                    {/* Page selector */}
                    {htmlPages.length > 1 && (
                        <select
                            value={currentFile}
                            onChange={(e) => {
                                setCurrentFile(e.target.value);
                                setExpandedSection(null);
                                setEditMode(null);
                            }}
                            className="bg-slate-800 text-slate-300 text-xs px-2 py-1 rounded border border-slate-600 focus:outline-none focus:border-purple-500"
                        >
                            {htmlPages.map((p) => (
                                <option key={p} value={p}>
                                    {p}
                                </option>
                            ))}
                        </select>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={fetchSections}
                        disabled={loading}
                        className="text-slate-400 hover:text-white hover:bg-slate-700/50"
                        title="Refresh sections"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    {/* Export ZIP */}
                    {projectId && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleExportZip}
                            disabled={exporting}
                            className="text-slate-400 hover:text-white hover:bg-slate-700/50"
                            title="Export deploy-ready ZIP"
                        >
                            <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
                        </Button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 text-purple-400 animate-spin" />
                        <span className="ml-2 text-sm text-slate-400">Loading sections…</span>
                    </div>
                )}

                {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                        <span className="text-sm text-red-300">{error}</span>
                    </div>
                )}

                {!loading && !error && sections.length === 0 && (
                    <div className="text-center py-12">
                        <FileText className="h-8 w-8 text-slate-600 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No editable sections found</p>
                        <p className="text-xs text-slate-600 mt-1">Build a site first, then edit its sections here</p>
                    </div>
                )}

                {sections.map((name) => (
                    <Card
                        key={name}
                        className={`bg-slate-900/80 border transition-colors ${expandedSection === name
                                ? 'border-purple-500/50 shadow-lg shadow-purple-500/10'
                                : 'border-slate-700/50 hover:border-slate-600'
                            }`}
                    >
                        {/* Section header */}
                        <button
                            onClick={() => handleToggle(name)}
                            className="w-full flex items-center justify-between px-4 py-3 text-left"
                        >
                            <div className="flex items-center gap-2">
                                {expandedSection === name ? (
                                    <ChevronDown className="h-4 w-4 text-purple-400" />
                                ) : (
                                    <ChevronRight className="h-4 w-4 text-slate-500" />
                                )}
                                <span className="text-sm font-medium text-slate-200">
                                    {formatSectionName(name)}
                                </span>
                            </div>
                            <span className="text-xs text-slate-500 font-mono">{name}</span>
                        </button>

                        {/* Expanded panel */}
                        {expandedSection === name && (
                            <div className="px-4 pb-4 space-y-3 border-t border-slate-700/50 pt-3">
                                {/* Action buttons */}
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        variant={editMode === 'html' ? 'default' : 'outline'}
                                        onClick={() => {
                                            setEditMode(editMode === 'html' ? null : 'html');
                                            setEditHtml(extractSectionHtml(name));
                                        }}
                                        className={
                                            editMode === 'html'
                                                ? 'bg-purple-600 hover:bg-purple-700 text-white text-xs'
                                                : 'border-slate-600 text-slate-300 hover:bg-slate-700 text-xs'
                                        }
                                    >
                                        <Edit3 className="h-3 w-3 mr-1" />
                                        Edit HTML
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant={editMode === 'regen' ? 'default' : 'outline'}
                                        onClick={() => setEditMode(editMode === 'regen' ? null : 'regen')}
                                        className={
                                            editMode === 'regen'
                                                ? 'bg-indigo-600 hover:bg-indigo-700 text-white text-xs'
                                                : 'border-slate-600 text-slate-300 hover:bg-slate-700 text-xs'
                                        }
                                    >
                                        <Wand2 className="h-3 w-3 mr-1" />
                                        AI Regenerate
                                    </Button>
                                </div>

                                {/* HTML Editor */}
                                {editMode === 'html' && (
                                    <div className="space-y-2">
                                        <textarea
                                            value={editHtml}
                                            onChange={(e) => setEditHtml(e.target.value)}
                                            rows={10}
                                            className="w-full bg-slate-800 text-slate-200 text-xs font-mono p-3 rounded-lg border border-slate-600 focus:outline-none focus:border-purple-500 resize-y"
                                            placeholder="Paste or edit section HTML…"
                                        />
                                        <div className="flex gap-2 justify-end">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setEditMode(null)}
                                                className="text-slate-400 text-xs"
                                            >
                                                <X className="h-3 w-3 mr-1" />
                                                Cancel
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={handleSaveHtml}
                                                disabled={saving}
                                                className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                                            >
                                                {saving ? (
                                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                                ) : (
                                                    <Save className="h-3 w-3 mr-1" />
                                                )}
                                                Save Section
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* AI Regeneration */}
                                {editMode === 'regen' && (
                                    <div className="space-y-2">
                                        <textarea
                                            value={regenInstruction}
                                            onChange={(e) => setRegenInstruction(e.target.value)}
                                            rows={3}
                                            className="w-full bg-slate-800 text-slate-200 text-sm p-3 rounded-lg border border-slate-600 focus:outline-none focus:border-indigo-500 resize-y"
                                            placeholder="Describe how this section should change… e.g. 'Make it more professional and add a client testimonial'"
                                        />
                                        <div className="flex gap-2 justify-end">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setEditMode(null)}
                                                className="text-slate-400 text-xs"
                                            >
                                                <X className="h-3 w-3 mr-1" />
                                                Cancel
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={handleRegenerate}
                                                disabled={saving || !regenInstruction.trim()}
                                                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                                            >
                                                {saving ? (
                                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                                ) : (
                                                    <Wand2 className="h-3 w-3 mr-1" />
                                                )}
                                                Regenerate with AI
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                ))}
            </div>

            {/* Footer – Export CTA */}
            {projectId && sections.length > 0 && (
                <div className="border-t border-slate-700/50 px-4 py-3 bg-slate-900/80">
                    <Button
                        onClick={handleExportZip}
                        disabled={exporting}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium"
                    >
                        {exporting ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Download className="h-4 w-4 mr-2" />
                        )}
                        Export Deploy-Ready ZIP
                    </Button>
                </div>
            )}
        </div>
    );
};

export default SectionEditor;
